/// <reference path="./embed.d.ts" />

/**
 * Web Component: <willba-filter-bar>
 * Registers a custom element backed by the WillbaFilterBar HTMLElement shim.
 * The actual customElements.define call is at the end of this file — it must
 * run last so all helpers are initialized before the browser synchronously
 * upgrades any <willba-filter-bar> already present in the DOM.
 */

import React from 'react'
import { createRoot, Root } from 'react-dom/client'

import { FilterBar } from './components/FilterBar'
import { FilterBarTypes } from './components/FilterBar/FilterBarTypes'

import EMBED_CSS from 'virtual:embed-css'

const FONT_LINK_ID = 'willba-embed-fonts'
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap'
const FILTER_BAR_API_PATH = '/api/services/filter-bar-config'
const REQUEST_TIMEOUT_MS = 10000
const ELEMENT_TAG = 'willba-filter-bar'
const SUPPORTED_LANGUAGES = ['fi', 'en']
const DEFAULT_LANGUAGE = 'fi'

let sharedStyleSheet: CSSStyleSheet | null = null
const supportsAdoptedStyleSheets =
  typeof Document !== 'undefined' && 'adoptedStyleSheets' in Document.prototype

const roots = new WeakMap<HTMLElement, Root>()

const mount = (host: HTMLElement) => {
  if (roots.has(host)) return

  ensureFonts()

  // Shadow roots persist on the host even after disconnectedCallback, so reuse
  // the existing one if the element is being re-attached to the DOM.
  if (!host.shadowRoot) {
    const shadow = host.attachShadow({ mode: 'open' })
    applyStyles(shadow)
    const container = document.createElement('div')
    container.className = 'willba-embed-root'
    if (host.hasAttribute('full-width')) {
      container.classList.add('is-full-width')
    }
    shadow.appendChild(container)
  }

  const container = host.shadowRoot!.querySelector(
    '.willba-embed-root'
  ) as HTMLDivElement
  roots.set(host, createRoot(container))

  const domain = host.getAttribute('domain')
  if (domain) loadConfigFromApi(host, domain)
}

const unmount = (host: HTMLElement) => {
  const root = roots.get(host)
  if (!root) return
  root.unmount()
  roots.delete(host)
}

class WillbaFilterBar extends HTMLElement {
  connectedCallback() {
    mount(this)
  }
  disconnectedCallback() {
    unmount(this)
  }
}

///////// Implementation

const appendLink = (attrs: Partial<HTMLLinkElement>) => {
  const link = Object.assign(document.createElement('link'), attrs)
  document.head.appendChild(link)
}

const ensureFonts = () => {
  if (typeof document === 'undefined') return
  if (document.getElementById(FONT_LINK_ID)) return

  appendLink({ rel: 'preconnect', href: 'https://fonts.googleapis.com' })
  appendLink({
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: '',
  })
  appendLink({ id: FONT_LINK_ID, rel: 'stylesheet', href: FONT_HREF })
}

const getOrCreateStyleSheet = (): CSSStyleSheet | null => {
  if (!supportsAdoptedStyleSheets) return null
  if (sharedStyleSheet) return sharedStyleSheet
  try {
    sharedStyleSheet = new CSSStyleSheet()
    sharedStyleSheet.replaceSync(EMBED_CSS)
    return sharedStyleSheet
  } catch {
    return null
  }
}

const applyStyles = (shadowRoot: ShadowRoot) => {
  const sheet = getOrCreateStyleSheet()
  if (sheet) {
    shadowRoot.adoptedStyleSheets = [sheet]
    return
  }
  const styleEl = document.createElement('style')
  styleEl.textContent = EMBED_CSS
  shadowRoot.appendChild(styleEl)
}

const isLocalDomain = (domain: string) =>
  domain.startsWith('localhost') ||
  domain.startsWith('127.') ||
  domain.startsWith('0.0.0.0')

const renderInto = (root: Root, props: Partial<FilterBarTypes>) => {
  root.render(<FilterBar {...(props as FilterBarTypes)} />)
}

const loadConfigFromApi = async (host: HTMLElement, domain: string) => {
  try {
    const protocol = isLocalDomain(domain) ? 'http' : 'https'
    const requested = host.getAttribute('language')
    const locale =
      requested && SUPPORTED_LANGUAGES.includes(requested)
        ? requested
        : DEFAULT_LANGUAGE
    const url = `${protocol}://${domain}${FILTER_BAR_API_PATH}?locale=${locale}`

    const res = await fetch(url, {
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    })
    if (!res.ok) return

    const body = await res.json()
    const filterBarConfig = body?.filterBarConfig
    if (!filterBarConfig) return

    const mode = host.getAttribute('mode')
    const modeOverride: Partial<FilterBarTypes> =
      mode === 'dark' || mode === 'light' ? { mode } : {}

    const fullWidthOverride: Partial<FilterBarTypes> = host.hasAttribute(
      'full-width'
    )
      ? { fullWidth: true }
      : {}

    const root = roots.get(host)
    if (!root) return

    renderInto(root, {
      ...filterBarConfig,
      redirectUrl: `${protocol}://${domain}/${locale}`,
      ...modeOverride,
      ...fullWidthOverride,
    })
  } catch (error) {
    console.error(`[${ELEMENT_TAG}] Failed to load config:`, error)
  }
}

///////// Custom Element Definition

if (typeof customElements !== 'undefined' && !customElements.get(ELEMENT_TAG)) {
  customElements.define(ELEMENT_TAG, WillbaFilterBar)
}
